import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useGlobal } from 'context/global/GlobalContext';
import { ReactComponent as SuccessIcon } from 'assets/icons/svg/toast-success.svg';
import { ReactComponent as CautionIcon } from 'assets/icons/svg/toast-caution.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/svg/toast-error.svg';
import { PossibleExperienceType } from 'types/ExperienceTypes';
declare global {
  interface Window {
    customToast?: {
      success: (
        message: string | React.ReactNode,
        options?: ToastOptions
      ) => string;
      error: (
        message: string | React.ReactNode,
        options?: ToastOptions
      ) => string;
      warn: (
        message: string | React.ReactNode,
        options?: ToastOptions
      ) => string;
    };
    customToastAdd?: (
      message: string | React.ReactNode,
      type: ToastType,
      options?: ToastOptions
    ) => string;
  }
}

type ToastType = 'success' | 'error' | 'warn';

interface ToastProps {
  id: string;
  message: string | React.ReactNode;
  type: ToastType;
  onClose: (id: string) => void;
  autoClose?: number | false;
}

interface ToastContainerProps {
  children?: React.ReactNode;
}

interface ToastOptions {
  autoClose?: number | false;
  icon?: React.ReactNode;
}

const ToastItem: React.FC<ToastProps> = ({
  id,
  message,
  type,
  onClose,
  autoClose = 4000,
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const getToastIcon = () => {
    switch (type) {
      case 'success':
        return <SuccessIcon className='w-6 h-6' />;
      case 'error':
        return <ErrorIcon className='w-6 h-6' />;
      case 'warn':
        return <CautionIcon className='w-6 h-6' />;
      default:
        return <SuccessIcon className='w-6 h-6' />;
    }
  };

  const getBgColor = () => {
    switch (type) {
      case 'success':
        return 'bg-toastSuccess';
      case 'error':
        return 'bg-toastError';
      case 'warn':
        return 'bg-toastWarn';
      default:
        return 'bg-toastSuccess';
    }
  };

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onClose(id), 300);
  }, [id, onClose]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (autoClose && autoClose > 0)
      timerRef.current = setTimeout(handleClose, autoClose);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [autoClose, handleClose]);

  return (
    <div
      className={`
        flex w-full items-center mb-3 pl-[5px] rounded-lg border border-solid border-toast cursor-pointer transform transition-all duration-300 overflow-hidden ${getBgColor()} 
        ${
          isExiting
            ? 'animate-toast-exit opacity-0 -translate-y-5 scale-95'
            : 'animate-toast-enter opacity-100 translate-y-0 scale-100'
        }
      `}
      style={{ boxShadow: `0 16px 40px rgba(0, 0, 0, 0.05)` }}
      onClick={handleClose}
      role='alert'
    >
      <div className='relative w-full h-full bg-white flex flex-row p-4 pl-[11px] gap-2'>
        <div className='flex-shrink-0'>{getToastIcon()}</div>
        <div className='text-sm font-medium leading-[20px] tracking-[0.1px] text-dark'>
          {message}
        </div>
      </div>
    </div>
  );
};

const ToastContainer: React.FC<ToastContainerProps> = () => {
  const [toasts, setToasts] = useState<
    Array<{
      id: string;
      message: string | React.ReactNode;
      type: ToastType;
      options?: ToastOptions;
    }>
  >([]);
  const { desktopView, productDetails } = useGlobal();

  const addToast = useCallback(
    (
      message: string | React.ReactNode,
      type: ToastType,
      options?: ToastOptions
    ) => {
      const id = Math.random().toString(36).substring(2, 9);
      setToasts((prev) => [...prev, { id, message, type, options }]);
      return id;
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    const toastFunctions = {
      success: (message: string | React.ReactNode, options?: ToastOptions) =>
        addToast(message, 'success', options),
      error: (message: string | React.ReactNode, options?: ToastOptions) =>
        addToast(message, 'error', options),
      warn: (message: string | React.ReactNode, options?: ToastOptions) =>
        addToast(message, 'warn', options),
    };
    window.customToast = toastFunctions;
    window.customToastAdd = addToast;
    return () => {
      delete window.customToast;
      delete window.customToastAdd;
    };
  }, [addToast]);

  const toastRoot = document.getElementById('toast-root');
  if (!toastRoot) return null;

  return createPortal(
    <div
      className={`w-[90%] max-w-[350px] fixed top-4 lg:top-8 left-1/2 transform -translate-x-1/2 z-[9999] flex flex-col items-center`}
      style={{
        top: desktopView
          ? window.innerHeight < 786
            ? '42px'
            : `${(window.innerHeight - 700) / 2}px`
          : '',
        transform: desktopView
          ? productDetails?.product.qrCodeDestination ===
            PossibleExperienceType.PRODUCT_EXPERIENCE
            ? 'translateX(-242px)'
            : ''
          : '',
      }}
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={removeToast}
          autoClose={
            toast.options?.autoClose !== undefined
              ? toast.options.autoClose
              : 4000
          }
        />
      ))}
    </div>,
    toastRoot
  );
};

if (!document.getElementById('toast-root')) {
  const toastRoot = document.createElement('div');
  toastRoot.id = 'toast-root';
  document.body.appendChild(toastRoot);
}

const style = document.createElement('style');
style.innerHTML = `
  @keyframes toast-enter {
    0% {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @keyframes toast-exit {
    0% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
  }
  
  .animate-toast-enter {
    animation: toast-enter 0.3s ease-out forwards;
  }
  
  .animate-toast-exit {
    animation: toast-exit 0.3s ease-in forwards;
  }
`;
document.head.appendChild(style);

const toast = {
  success: (message: string | React.ReactNode, options?: ToastOptions) => {
    return (
      window.customToast?.success?.(message, options) ||
      window.customToastAdd?.(message, 'success', options)
    );
  },
  error: (message: string | React.ReactNode, options?: ToastOptions) => {
    return (
      window.customToast?.error?.(message, options) ||
      window.customToastAdd?.(message, 'error', options)
    );
  },
  warn: (message: string | React.ReactNode, options?: ToastOptions) => {
    return (
      window.customToast?.warn?.(message, options) ||
      window.customToastAdd?.(message, 'warn', options)
    );
  },
};

type ToastPropsAPI = {
  message: string | React.ReactNode;
  type: 'success' | 'error' | 'warn';
};

export const showToast = (props: ToastPropsAPI) => {
  switch (props.type) {
    case 'success':
      return toast.success(props.message, {
        icon: <SuccessIcon />,
      });
    case 'error':
      return toast.error(props.message, {
        icon: <ErrorIcon />,
      });
    case 'warn':
      return toast.warn(props.message, {
        icon: <CautionIcon />,
      });
  }
};

export const Toast: React.FC = () => <ToastContainer />;

export default Toast;
